// src/services/userService.js
import { authenticatedFetch } from './api'; // Assuming your shared API helper
// Removed unused config import as authenticatedFetch should handle the base API URL

const API_USERS_URL = '/users'; // Relative to your API base configured in api.js

/**
 * Fetches a list of users.
 * @param {object} [params={}] - Optional query parameters (e.g., { role: 'tech', page: 1, limit: 100, search: 'searchText' }).
 * @returns {Promise<any>} The response from the backend, typically { data: [], pagination: {} }.
 */
export const fetchUsers = async (params = {}) => {
    // Create a new URLSearchParams object.
    // Iterate over the params object and append only if the value is not null or undefined.
    const queryParams = new URLSearchParams();
    for (const key in params) {
        if (params[key] !== null && params[key] !== undefined) {
            queryParams.append(key, params[key]);
        }
    }
    const queryString = queryParams.toString();
    const endpoint = `${API_USERS_URL}${queryString ? `?${queryString}` : ''}`;

    console.log(`[UserService] Fetching users from: ${endpoint}`);
    try {
        const responseData = await authenticatedFetch(endpoint);
        // Ensure a consistent return structure, especially if the backend might omit 'data' or 'pagination' on empty results.
        return {
            data: responseData.data || [],
            pagination: responseData.pagination || { currentPage: 1, limit: params.limit || 10, totalItems: 0, totalPages: 0 }
        };
    } catch (error) {
        console.error("[UserService] Error fetching users:", error);
        // It's good practice to re-throw or handle the error in a way that the UI can react.
        // For example, returning an empty state or a specific error object.
        // For now, re-throwing:
        throw error;
    }
};

/**
 * Fetches a single user by their ID.
 * @param {string} userId - The ID of the user to fetch.
 * @returns {Promise<any>} The user object.
 */
export const fetchUserById = async (userId) => {
    if (!userId) {
        console.error("[UserService] fetchUserById: userId is required.");
        throw new Error("User ID is required.");
    }
    const endpoint = `${API_USERS_URL}/${userId}`;
    console.log(`[UserService] Fetching user by ID: ${userId}`);
    try {
        const responseData = await authenticatedFetch(endpoint);
        return responseData; // Assuming backend returns the user object directly
    } catch (error) {
        console.error(`[UserService] Error fetching user ${userId}:`, error);
        throw error;
    }
};

/**
 * Updates a user.
 * @param {string} userId - The ID of the user to update.
 * @param {object} userData - The data to update.
 * @returns {Promise<any>} The response from the backend.
 */
export const updateUser = async (userId, userData) => {
    if (!userId) {
        console.error("[UserService] updateUser: userId is required.");
        throw new Error("User ID is required.");
    }
    const endpoint = `${API_USERS_URL}/${userId}`;
    console.log(`[UserService] Updating user ${userId}`);
    const options = {
        method: 'PUT',
        body: JSON.stringify(userData),
    };
    try {
        const responseData = await authenticatedFetch(endpoint, options);
        return responseData; // Assuming backend returns { message: '...', user: {...} }
    } catch (error) {
        console.error(`[UserService] Error updating user ${userId}:`, error);
        throw error;
    }
};

/**
 * Deletes a user.
 * @param {string} userId - The ID of the user to delete.
 * @returns {Promise<any>} The response from the backend.
 */
export const deleteUser = async (userId) => {
    if (!userId) {
        console.error("[UserService] deleteUser: userId is required.");
        throw new Error("User ID is required.");
    }
    const endpoint = `${API_USERS_URL}/${userId}`;
    console.log(`[UserService] Deleting user ${userId}`);
    const options = {
        method: 'DELETE',
    };
    try {
        const responseData = await authenticatedFetch(endpoint, options);
        return responseData; // Assuming backend returns { message: '...' }
    } catch (error) {
        console.error(`[UserService] Error deleting user ${userId}:`, error);
        throw error;
    }
};

/**
 * Updates a user's skills by their ID.
 * @param {string} userId - The ID of the user.
 * @param {string[]} skillsArray - The new array of skills.
 * @returns {Promise<Object>} The server response.
 */
export const updateUserSkills = async (userId, skillsArray) => {
  const endpoint = `${API_USERS_URL}/${userId}/skills`;
  console.log(`[UserService] Updating skills for user ${userId}`);
  
  const options = {
    method: 'PUT',
    body: JSON.stringify({ skills: skillsArray }),
    // 'authenticatedFetch' should automatically add 'Content-Type: application/json' header
  };

  try {
    // Use the 'authenticatedFetch' helper, just like the other functions in this file
    const responseData = await authenticatedFetch(endpoint, options);
    return responseData; // { message: '...', user: {...} }
  } catch (error) {
    console.error(`Error updating user ${userId} skills:`, error);
    throw new Error(error.message || 'Failed to update user skills');
  }
};

// Note: User creation (registration) is typically handled in authService.js or a dedicated registration service.
// If you need an admin function to create users, it can be added here.